import {
  ParticipantResponseSchema,
  type ConversationOption,
  type ConversationQuestion,
  type ConversationTemplate,
  type ParticipantResponse,
  type SummaryCategory,
} from "@samklang/schemas";

interface ConversationData {
  readonly responses: Readonly<Record<string, ParticipantResponse>>;
  readonly orphanedResponseIds: readonly string[];
  readonly maxQuestions: number | undefined;
  readonly triggeredSafetyRuleIds: readonly string[];
  readonly excludedQuestionKinds: readonly ConversationQuestion["kind"][];
}

export type ConversationState =
  | ({ status: "idle" } & ConversationData)
  | ({ status: "active"; currentQuestionId: string } & ConversationData)
  | ({ status: "completed" } & ConversationData);

export interface StartConversationOptions { readonly maxQuestions?: number; readonly excludedQuestionKinds?: readonly ConversationQuestion["kind"][] }

export interface SummaryItem {
  readonly questionId: string;
  readonly question: string;
  readonly topicLabel: string;
  readonly answer: string;
  readonly theme: string;
  readonly category: SummaryCategory;
  readonly source: "participant";
  readonly orphaned: boolean;
  readonly metricKey?: string;
  readonly metricLabel?: string;
  readonly metricValue?: number;
  readonly metricDirection?: "higher_is_better" | "higher_is_more";
}

const emptyData: ConversationData = {
  responses: {},
  orphanedResponseIds: [],
  maxQuestions: undefined,
  triggeredSafetyRuleIds: [],
  excludedQuestionKinds: [],
};

export function createIdleConversation(): ConversationState { return { status: "idle", ...emptyData }; }

export function startConversation(template: ConversationTemplate, options: StartConversationOptions = {}): ConversationState {
  const data = { ...emptyData, maxQuestions: options.maxQuestions, excludedQuestionKinds: options.excludedQuestionKinds ?? [] };
  const firstQuestion = getVisibleQuestions(template, data.responses, options.maxQuestions, data.excludedQuestionKinds)[0];
  return firstQuestion ? { status: "active", currentQuestionId: firstQuestion.id, ...data } : { status: "completed", ...data };
}

function responseIncludesOption(response: ParticipantResponse | undefined, optionId: string): boolean {
  if (!response) return false;
  if ("optionId" in response) return response.optionId === optionId;
  if ("optionIds" in response) return response.optionIds.includes(optionId);
  return false;
}

function isVisible(question: ConversationQuestion, responses: Readonly<Record<string, ParticipantResponse>>): boolean {
  const rule = question.showWhen;
  if (!rule) return true;
  const response = responses[rule.questionId];
  if (rule.answerKind === "answered") return Boolean(response && response.kind !== "skipped");
  if (rule.answerKind === "skipped") return response?.kind === "skipped";
  return rule.includesOptionId ? responseIncludesOption(response, rule.includesOptionId) : Boolean(response);
}

export function getVisibleQuestions(
  template: ConversationTemplate,
  responses: Readonly<Record<string, ParticipantResponse>>,
  maxQuestions?: number,
  excludedQuestionKinds: readonly ConversationQuestion["kind"][] = [],
): readonly ConversationQuestion[] {
  const excluded = new Set(excludedQuestionKinds);
  const visible = template.questions.filter((question) => !excluded.has(question.kind) && isVisible(question, responses));
  if (!maxQuestions || visible.length <= maxQuestions) return visible;
  const included = new Set(
    [...visible]
      .sort((left, right) => right.priority - left.priority)
      .slice(0, maxQuestions)
      .map((question) => question.id),
  );
  return visible.filter((question) => included.has(question.id));
}

export function getCurrentQuestion(template: ConversationTemplate, state: ConversationState): ConversationQuestion | undefined {
  if (state.status !== "active") return undefined;
  return template.questions.find((question) => question.id === state.currentQuestionId);
}

function optionList(question: ConversationQuestion): readonly ConversationOption[] {
  if ("options" in question) return question.options;
  if (question.kind === "person_map") return question.people;
  return [];
}

function validateResponseForQuestion(question: ConversationQuestion, response: ParticipantResponse): void {
  if (question.id !== response.questionId) throw new Error("Svaret tillhör inte den aktuella frågan.");
  if (response.kind === "skipped") {
    if (response.reason === "unknown" && !question.allowUnknown) throw new Error("Vet inte är inte tillgängligt för frågan.");
    if (response.reason === "declined" && !question.allowDecline) throw new Error("Vill inte svara är inte tillgängligt för frågan.");
    if (response.reason === "skipped" && !question.allowSkip) throw new Error("Frågan kan inte hoppas över.");
    return;
  }
  if (question.kind !== response.kind) throw new Error("Svarssättet passar inte frågan.");

  if ("optionId" in response) {
    const selected = optionList(question).find((option) => option.id === response.optionId);
    if (!selected) throw new Error("Svarsalternativet finns inte.");
    if (response.otherText && !selected.isOther) throw new Error("Fritext kan bara kopplas till alternativet Annat.");
  }
  if (question.kind === "multi_choice" && response.kind === "multi_choice") {
    const allowedIds = new Set(question.options.map((option) => option.id));
    if (response.optionIds.some((id) => !allowedIds.has(id))) throw new Error("Ett eller flera svarsalternativ finns inte.");
    if (question.maxSelections && response.optionIds.length > question.maxSelections) throw new Error("För många svarsalternativ valdes.");
    const selectedOther = question.options.some((option) => option.isOther && response.optionIds.includes(option.id));
    if (response.otherText && !selectedOther) throw new Error("Fritext kan bara kopplas till alternativet Annat.");
  }
  if (question.kind === "ranking" && response.kind === "ranking") {
    const allowedIds = new Set(question.options.map((option) => option.id));
    if (new Set(response.optionIds).size !== response.optionIds.length || response.optionIds.some((id) => !allowedIds.has(id))) throw new Error("Sorteringen innehåller ogiltiga alternativ.");
  }
  if (question.kind === "scale" && response.kind === "scale" && (response.value < question.min || response.value > question.max)) throw new Error("Skalvärdet ligger utanför frågans intervall.");
  if (question.kind === "text" && response.kind === "text" && response.text.length > question.maxLength) throw new Error("Texten är för lång.");
  if (question.kind === "person_map" && response.kind === "person_map") {
    const allowedIds = new Set(question.people.map((person) => person.id));
    if (Object.keys(response.placements).some((id) => !allowedIds.has(id))) throw new Error("Personkartan innehåller en okänd person.");
  }
}

function safetyRules(question: ConversationQuestion, response: ParticipantResponse, previous: readonly string[]): readonly string[] {
  if (!question.safetyRuleId || !("optionId" in response)) return previous;
  const selected = optionList(question).find((option) => option.id === response.optionId);
  if (selected?.tone !== "negative") return previous;
  return previous.includes(question.safetyRuleId) ? previous : [...previous, question.safetyRuleId];
}

export function answerQuestion(template: ConversationTemplate, state: ConversationState, rawResponse: ParticipantResponse): ConversationState {
  if (state.status !== "active") throw new Error("Samtalet är inte aktivt.");
  const response = ParticipantResponseSchema.parse(rawResponse);
  const question = getCurrentQuestion(template, state);
  if (!question) throw new Error("Den aktuella frågan saknas.");
  validateResponseForQuestion(question, response);

  const responses = { ...state.responses, [question.id]: response };
  const visible = getVisibleQuestions(template, responses, state.maxQuestions, state.excludedQuestionKinds);
  const visibleIds = new Set(visible.map((item) => item.id));
  const orphanedResponseIds = Object.keys(responses).filter((id) => !visibleIds.has(id));
  const currentIndex = visible.findIndex((item) => item.id === question.id);
  const nextQuestion = visible[currentIndex + 1];
  const data: ConversationData = {
    responses,
    orphanedResponseIds,
    maxQuestions: state.maxQuestions,
    triggeredSafetyRuleIds: safetyRules(question, response, state.triggeredSafetyRuleIds),
    excludedQuestionKinds: state.excludedQuestionKinds,
  };
  return nextQuestion ? { status: "active", currentQuestionId: nextQuestion.id, ...data } : { status: "completed", ...data };
}

export function goToPreviousQuestion(template: ConversationTemplate, state: ConversationState): ConversationState {
  if (state.status !== "active") return state;
  const visible = getVisibleQuestions(template, state.responses, state.maxQuestions, state.excludedQuestionKinds);
  const currentIndex = visible.findIndex((item) => item.id === state.currentQuestionId);
  const previous = visible[currentIndex - 1];
  return previous ? { ...state, currentQuestionId: previous.id } : state;
}

function optionLabels(question: ConversationQuestion, ids: readonly string[]): string {
  const options = optionList(question);
  return ids.map((id) => options.find((option) => option.id === id)?.label).filter((label): label is string => Boolean(label)).join(", ");
}

function formatAnswer(question: ConversationQuestion, response: ParticipantResponse): string {
  if (response.kind === "skipped") {
    if (response.reason === "unknown") return "Vet inte";
    if (response.reason === "declined") return "Vill inte svara";
    return "Hoppade över";
  }
  if (response.kind === "scale" && question.kind === "scale") return `${response.value} av ${question.max}`;
  if ("optionId" in response) {
    const label = optionLabels(question, [response.optionId]) || "Okänt svar";
    return response.otherText ? `${label}: ${response.otherText}` : label;
  }
  if (response.kind === "multi_choice") {
    const labels = optionLabels(question, response.optionIds);
    return response.otherText ? `${labels}: ${response.otherText}` : labels;
  }
  if (response.kind === "ranking") return optionLabels(question, response.optionIds);
  if (response.kind === "text" || response.kind === "tell" || response.kind === "previous_answer") return response.text || "Inget tillagt";
  if (response.kind === "drawing") return "Ritning";
  if (response.kind === "person_map") return Object.entries(response.placements).map(([id, ring]) => `${optionLabels(question, [id])}: ring ${ring}`).join(", ");
  return "Okänt svar";
}

export function createSummary(template: ConversationTemplate, state: ConversationState): readonly SummaryItem[] {
  return template.questions.flatMap((question) => {
    const response = state.responses[question.id];
    if (!response) return [];
    const metric = question.kind === "scale" && response.kind === "scale" && question.metric ? {
      metricKey: question.metric.key,
      metricLabel: question.metric.label,
      metricValue: response.value,
      metricDirection: question.metric.direction,
    } : {};
    return [{
      questionId: question.id,
      question: question.title,
      topicLabel: question.shortTitle ?? question.metric?.label ?? question.theme,
      answer: formatAnswer(question, response),
      theme: question.theme,
      category: question.summaryMapping.category,
      source: "participant" as const,
      orphaned: state.orphanedResponseIds.includes(question.id),
      ...metric,
    }];
  });
}

export function questionBudgetForPace(pace: "standard" | "deep" | "short"): number {
  if (pace === "deep") return 35;
  // "short" (Snabbkoll) is retired; legacy saved values map to the standard budget.
  return 17;
}

/** Resolve the wording used to present a question for a communication level and a perspective. */
export interface QuestionWordingOptions {
  readonly communicationLevel?: "enkel" | "standard" | "younger" | "older" | "teen" | "adult" | "custom";
  readonly perspective?: "elev" | "mentor" | "vardnadshavare" | "annan";
  readonly shortest?: boolean;
}

export function resolveQuestionWording(question: ConversationQuestion, options: QuestionWordingOptions = {}): string {
  const perspectiveText = options.perspective ? question.wordingByPerspective?.[options.perspective] : undefined;
  if (perspectiveText) return perspectiveText;
  const level = options.communicationLevel;
  if (level === "enkel" || level === "younger" || level === "older") {
    const enkel = question.wordingByAudience?.enkel ?? question.wordingByAudience?.younger ?? question.wordingByAudience?.older;
    if (enkel) return enkel;
  }
  if (level === "standard" || level === "teen" || level === "adult") {
    const standard = question.wordingByAudience?.standard ?? question.wordingByAudience?.teen ?? question.wordingByAudience?.adult;
    if (standard) return standard;
  }
  if (options.shortest && question.alternativeWording[0]) return question.alternativeWording[0];
  return question.title;
}
